'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

interface Listing {
  id: string;
  title: string;
  slug: string;
  city: string;
  full_address: string;
  price_per_sqft: number | null;
  total_price: number | null;
  listing_type: string;
  bhk_details: string | null;
  area_sqft: number | null;
  description: string | null;
  cover_image_url: string | null;
  listing_status: string;
  is_featured: boolean;
  amenities: string[];
  youtube_videos: any[];
  created_at: string;
}

interface Props {
  listings: Listing[];
}

const STATUS_COLORS: Record<string, string> = {
  available: 'bg-green-100 text-green-700',
  sold_out: 'bg-red-100 text-red-700',
  coming_soon: 'bg-blue-100 text-blue-700',
};

export default function PropertyListingClient({ listings }: Props) {
  const [filterType, setFilterType] = useState('all');
  const [filterCity, setFilterCity] = useState('all');
  const [filterPrice, setFilterPrice] = useState('all');

  const cities = useMemo(() => {
    const unique = [...new Set(listings.map(l => l.city))];
    return unique;
  }, [listings]);

  const filtered = useMemo(() => {
    return listings.filter(l => {
      if (filterType !== 'all' && l.listing_type !== filterType) return false;
      if (filterCity !== 'all' && l.city !== filterCity) return false;
      if (filterPrice !== 'all') {
        const price = l.total_price || 0;
        if (filterPrice === 'under50' && price >= 5000000) return false;
        if (filterPrice === '50to1cr' && (price < 5000000 || price >= 10000000)) return false;
        if (filterPrice === 'above1cr' && price < 10000000) return false;
      }
      return true;
    });
  }, [listings, filterType, filterCity, filterPrice]);

  const formatPrice = (l: Listing) => {
    if (l.total_price) {
      if (l.total_price >= 10000000) return `₹${(l.total_price / 10000000).toFixed(1)} Cr`;
      if (l.total_price >= 100000) return `₹${(l.total_price / 100000).toFixed(1)} L`;
      return `₹${l.total_price.toLocaleString('en-IN')}`;
    }
    if (l.price_per_sqft) return `₹${l.price_per_sqft.toLocaleString('en-IN')}/sqft`;
    return 'Price on Request';
  };

  return (
    <>
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 py-20 px-4">
        <div className="container mx-auto text-center">
          <p className="text-yellow-400 text-xs font-bold uppercase tracking-widest mb-4">ARTI Enterprise</p>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 font-serif">
            Find Your <span className="text-yellow-400">Dream Property</span>
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto">
            Verified listings across Kolkata. Apartments, plots, villas and commercial spaces at honest prices.
          </p>
          <div className="mt-6 text-slate-400 text-sm">{listings.length} properties listed</div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-slate-100 sticky top-20 z-30 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-wrap gap-3 items-center">
            <span className="text-sm font-semibold text-slate-600 mr-2">Filter:</span>
            <select
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-white"
            >
              <option value="all">All Types</option>
              <option value="apartment">Apartment</option>
              <option value="plot">Plot</option>
              <option value="villa">Villa</option>
              <option value="commercial">Commercial</option>
            </select>
            <select
              value={filterCity}
              onChange={e => setFilterCity(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-white"
            >
              <option value="all">All Locations</option>
              {cities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={filterPrice}
              onChange={e => setFilterPrice(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-white"
            >
              <option value="all">Any Price</option>
              <option value="under50">Under ₹50 Lakh</option>
              <option value="50to1cr">₹50L – ₹1 Cr</option>
              <option value="above1cr">Above ₹1 Cr</option>
            </select>
            {(filterType !== 'all' || filterCity !== 'all' || filterPrice !== 'all') && (
              <button
                onClick={() => { setFilterType('all'); setFilterCity('all'); setFilterPrice('all'); }}
                className="text-sm text-red-500 hover:text-red-700 font-medium"
              >Clear Filters</button>
            )}
            <span className="ml-auto text-sm text-slate-500">{filtered.length} results</span>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-12 bg-slate-50">
        <div className="container mx-auto px-4">
          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-6xl mb-4">🏠</div>
              <h3 className="text-xl font-bold text-slate-700 mb-2">No properties found</h3>
              <p className="text-slate-500">Try adjusting your filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map(l => (
                <Link
                  key={l.id}
                  href={`/properties/${l.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100 hover:shadow-lg hover:border-yellow-200 transition-all duration-300"
                >
                  {/* Image */}
                  <div className="relative aspect-[4/3] bg-slate-100 overflow-hidden">
                    {l.cover_image_url ? (
                      <AppImage
                        src={l.cover_image_url}
                        alt={`${l.title} - ${l.city} property`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-200 to-slate-300">
                        <span className="text-5xl">🏠</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute top-3 left-3 flex gap-2">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${STATUS_COLORS[l.listing_status] || 'bg-slate-100 text-slate-700'}`}>
                        {l.listing_status.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                      {l.is_featured && (
                        <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-yellow-400 text-slate-900">⭐ Featured</span>
                      )}
                    </div>
                    {l.youtube_videos?.length > 0 && (
                      <div className="absolute bottom-3 right-3 bg-red-600 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                        Video Tour
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-bold text-slate-900 text-lg leading-tight group-hover:text-yellow-600 transition-colors">{l.title}</h3>
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full capitalize flex-shrink-0">{l.listing_type}</span>
                    </div>
                    <p className="text-slate-500 text-sm flex items-center gap-1.5 mb-3">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {l.full_address}
                    </p>
                    {l.description && (
                      <p className="text-slate-500 text-sm line-clamp-2 mb-4">{l.description}</p>
                    )}
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <div>
                        <div className="text-yellow-600 font-bold text-lg">{formatPrice(l)}</div>
                        {l.bhk_details && <div className="text-xs text-slate-400">{l.bhk_details}{l.area_sqft ? ` · ${l.area_sqft} sqft` : ''}</div>}
                      </div>
                      <span className="text-yellow-600 group-hover:translate-x-1 transition-transform">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
